import { useNavigate } from 'react-router-dom';
import { useThemeStore } from '@/store/themeStore';
import { Tooltip } from '@/components/ui/Tooltip';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import { ChatCenter } from '@/components/chat/ChatCenter';

interface TopBarProps {
  onMenuClick?: () => void;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const { theme, toggleTheme } = useThemeStore();
  const navigate = useNavigate();

  return (
    <header className="h-16 border-b border-slate-200 dark:border-border-dark bg-white dark:bg-surface-dark px-4 lg:px-8 flex items-center justify-between flex-shrink-0 z-10">
      {/* Hamburger menu — tablet only */}
      <div className="lg:hidden">
        <Tooltip text="Toggle menu" position="bottom">
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
          >
            <span className="material-symbols-outlined text-xl">menu</span>
          </button>
        </Tooltip>
      </div>

      {/* Search */}
      <div className="flex-1 max-w-2xl mx-2 lg:mx-0">
        <div className="relative group">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors text-xl">
            search
          </span>
          <input
            className="w-full bg-slate-100 dark:bg-white/5 border-none outline-none focus:ring-2 focus:ring-primary rounded-xl pl-10 pr-4 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
            placeholder="Search customers, vehicles, or work orders..."
            type="text"
            aria-label="Search"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 ml-4">
        <ChatCenter />
        <NotificationCenter />
        <Tooltip text="Create a new repair work order" position="bottom">
          <button
            onClick={() => navigate('/workorders')}
            title="Create a new repair work order"
            className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-semibold transition-all shadow-lg shadow-orange-500/20"
          >
            <span className="material-symbols-outlined text-xl">add_circle</span>
            New Work Order
          </button>
        </Tooltip>

        <Tooltip text={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} position="bottom">
          <button
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="p-2 rounded-xl bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/20 transition-colors"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <span className="material-symbols-outlined text-xl">
              {theme === 'dark' ? 'light_mode' : 'dark_mode'}
            </span>
          </button>
        </Tooltip>
      </div>
    </header>
  );
}
